import React, { useState, useEffect } from 'react';
import { Plus, Search, Filter, Trash2, Edit2, TrendingDown, TrendingUp, Calendar } from 'lucide-react';
import { supabase } from '../utils/supabaseClient';
import { useAuth } from '../utils/AuthContext';
import { toast } from 'react-toastify';
import ExpenseModal from '../components/ExpenseModal';

const ExpenseTracker = () => {
    const { user } = useAuth();
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [expenseToEdit, setExpenseToEdit] = useState(null);

    // Filters
    const [categoryFilter, setCategoryFilter] = useState('All');
    const [startDate, setStartDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]); // First day of month
    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]); // Today

    useEffect(() => {
        if (user) {
            fetchExpenses();
        }
    }, [user, startDate, endDate, categoryFilter]);

    const fetchExpenses = async () => {
        setLoading(true);
        try {
            let query = supabase
                .from('expenses')
                .select('*')
                .eq('user_id', user.id)
                .gte('expense_date', startDate)
                .lte('expense_date', endDate)
                .order('expense_date', { ascending: false });

            if (categoryFilter !== 'All') {
                query = query.eq('category', categoryFilter);
            }

            const { data, error } = await query;
            if (error) throw error;
            setExpenses(data || []);
        } catch (error) {
            console.error('Error fetching expenses:', error);
            toast.error('Failed to load expenses');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this expense?")) return;
        try {
            const { error } = await supabase.from('expenses').delete().eq('id', id);
            if (error) throw error;
            toast.success('Expense deleted');
            fetchExpenses();
        } catch (error) {
            toast.error('Failed to delete expense');
        }
    };

    const handleEdit = (expense) => {
        setExpenseToEdit(expense);
        setIsModalOpen(true);
    };

    const openAddModal = () => {
        setExpenseToEdit(null);
        setIsModalOpen(true);
    };

    // Calculate Summary Stats
    const totalExpense = expenses.reduce((sum, item) => sum + parseFloat(item.amount), 0);

    // Group by category to find top expense
    const categoryTotals = expenses.reduce((acc, curr) => {
        acc[curr.category] = (acc[curr.category] || 0) + parseFloat(curr.amount);
        return acc;
    }, {});

    const topCategory = Object.keys(categoryTotals).reduce((a, b) => categoryTotals[a] > categoryTotals[b] ? a : b, '-');
    const topCategoryAmount = categoryTotals[topCategory] || 0;

    const categories = [
        'Staff Payment',
        'Transport Expense',
        'Office',
        'Maintenance',
        'Miscellaneous'
    ];

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                        <TrendingDown className="mr-2 h-6 w-6 text-red-600" />
                        Advance Expense Tracker
                    </h1>
                    <p className="text-gray-600">Track and manage business operational costs</p>
                </div>
                <button
                    onClick={openAddModal}
                    className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 shadow-sm transition-colors"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Expense
                </button>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-red-500">
                    <p className="text-sm font-medium text-gray-500">Total Expenses (Selected Period)</p>
                    <p className="mt-2 text-2xl font-bold text-gray-900">₹{totalExpense.toLocaleString('en-IN')}</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-orange-400">
                    <p className="text-sm font-medium text-gray-500">Top Category</p>
                    <div className="mt-2 flex items-baseline">
                        <p className="text-2xl font-bold text-gray-900 mr-2">{topCategory}</p>
                        <p className="text-sm text-gray-500">₹{topCategoryAmount.toLocaleString('en-IN')}</p>
                    </div>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-blue-400">
                    <p className="text-sm font-medium text-gray-500">Expense Count</p>
                    <p className="mt-2 text-2xl font-bold text-gray-900">{expenses.length}</p>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 flex flex-wrap gap-4 items-end">
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">From Date</label>
                    <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="block w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-red-500 focus:border-red-500"
                    />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">To Date</label>
                    <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="block w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-red-500 focus:border-red-500"
                    />
                </div>
                <div className="min-w-[150px]">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Category</label>
                    <select
                        value={categoryFilter}
                        onChange={(e) => setCategoryFilter(e.target.value)}
                        className="block w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-red-500 focus:border-red-500"
                    >
                        <option value="All">All Categories</option>
                        {categories.map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Expense Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mode</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">Loading...</td>
                                </tr>
                            ) : expenses.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="px-6 py-12 text-center text-gray-400">
                                        <div className="flex flex-col items-center">
                                            <Calendar className="w-12 h-12 mb-2 opacity-20" />
                                            <p>No expenses found for this period.</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                expenses.map((item) => (
                                    <tr key={item.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {new Date(item.expense_date).toLocaleDateString('en-GB')}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                                                {item.category}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate" title={item.notes}>
                                            {item.notes || '-'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {item.payment_mode}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                                            ₹{parseFloat(item.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                                            <button
                                                onClick={() => handleEdit(item)}
                                                className="text-blue-600 hover:text-blue-900 mr-3"
                                                title="Edit"
                                            >
                                                <Edit2 className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(item.id)}
                                                className="text-red-400 hover:text-red-600"
                                                title="Delete"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <ExpenseModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSave={fetchExpenses}
                expenseToEdit={expenseToEdit}
            />
        </div>
    );
};

export default ExpenseTracker;
